import React, { useEffect, useRef } from "react";

/** Animated star/particle field rendered on a fixed canvas behind the page. */
export default function Starfield({ density = 90, color = "rgba(196, 181, 253, 0.9)" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let raf;
    let stars = [];

    const resize = () => {
      canvas.width = window.innerWidth * window.devicePixelRatio;
      canvas.height = window.innerHeight * window.devicePixelRatio;
      canvas.style.width = window.innerWidth + "px";
      canvas.style.height = window.innerHeight + "px";
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      stars = Array.from({ length: density }).map(() => ({
        x: Math.random() * window.innerWidth,
        y: Math.random() * window.innerHeight,
        r: Math.random() * 1.4 + 0.2,
        vx: (Math.random() - 0.5) * 0.08,
        vy: (Math.random() - 0.5) * 0.08,
        a: Math.random() * 0.7 + 0.2,
        flicker: Math.random() * 0.02,
      }));
    };

    const tick = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      for (const s of stars) {
        s.x += s.vx; s.y += s.vy; s.a += (Math.random() - 0.5) * s.flicker;
        if (s.a < 0.1) s.a = 0.1;
        if (s.a > 0.95) s.a = 0.95;
        if (s.x < 0) s.x = window.innerWidth;
        if (s.x > window.innerWidth) s.x = 0;
        if (s.y < 0) s.y = window.innerHeight;
        if (s.y > window.innerHeight) s.y = 0;
        ctx.beginPath();
        ctx.fillStyle = color.replace(/0\.\d+\)/, s.a + ")");
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(tick);
    };

    resize();
    tick();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, [density, color]);

  return (
    <canvas
      ref={ref}
      aria-hidden
      style={{
        position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, opacity: 0.55,
        mixBlendMode: "screen",
      }}
    />
  );
}
